<script setup lang="ts">
import BaseHeader from '@/components/BaseHeader.vue';
import BaseSidebar from '@/components/BaseSidebar.vue';
import { useLayoutStore } from '@/stores/layout';
import { useSettingsStore } from '@/stores/settingsStore';
import { useThemeStore } from '@/stores/themeStore';
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { Toaster, toast } from 'vue-sonner';
import { initializeLocale } from './i18n';
import { authService } from './services/authService';
import { useUserStore } from './stores/user';
import { useI18n } from 'vue-i18n';
import { wartungsfensterService } from './services/wartungsfensterService';
import { useWartungsfensterStore } from './stores/wartungsfensterStore';
import WartungsBanner from './components/WartungsBanner.vue';
import { DnDProvider } from '@vue-dnd-kit/core';

const { t } = useI18n();

const layoutStore = useLayoutStore();
const route = useRoute();
const settingsStore = useSettingsStore();
const userStore = useUserStore();
const themeStore = useThemeStore();
const wartungsfensterStore = useWartungsfensterStore();

// Prüfen, ob wir uns auf der Home oder Login Seite befinden
const isPublicRoute = computed(() => {
  return ['home', 'login', 'not-found'].includes(route.name as string) || route.meta.isPublic;
});

onMounted(async () => {
  themeStore.initTheme();

  // Prüfen, ob ein Redirect-Loop erkannt wurde
  const isInLoop = sessionStorage.getItem('redirected_to_login') === 'true';

  // Wenn ein Loop erkannt wurde, automatisch zurücksetzen
  if (isInLoop) {
    console.log('Redirect-Loop erkannt, automatisches Zurücksetzen');
    authService.resetRedirectStatus();

    // Einstellungen nach dem Zurücksetzen nicht laden, um erneute Loops zu vermeiden
    await settingsStore.loadSettings({ skipApiCall: true });
  } else {
    // Normales Laden der Einstellungen
    await settingsStore.loadSettings();
  }

  initializeLocale();

  // Kleine Verzögerung, um sicherzustellen, dass der Token gespeichert wird
  await new Promise((resolve) => setTimeout(resolve, 500));

  // Prüfe beim Start, ob der Token noch gültig ist
  const isValid = await authService.isAuthenticated();

  // Wenn der Token nicht gültig ist und wir nicht auf einer öffentlichen Route sind,
  // umleiten zur Login-Seite
  if (!isValid && !isPublicRoute.value && route.name !== 'login' && route.name !== 'login-lang') {
    console.log('Token ist ungültig - Weiterleitung zum Login', {
      isValid,
      currentRoute: route.name,
      isPublicRoute: isPublicRoute.value,
      hasToken: !!authService.getToken(),
      hasMandant: !!authService.getMandant()
    });

    userStore.clearUserInfo();

    // Prüfen, ob bereits zur Login-Seite weitergeleitet wurde
    const redirectedToLogin = sessionStorage.getItem('redirected_to_login') === 'true';

    // Nur weiterleiten, wenn noch nicht weitergeleitet wurde
    if (!redirectedToLogin) {
      // Flag setzen, dass wir bereits weitergeleitet haben
      sessionStorage.setItem('redirected_to_login', 'true');

      // Kurze Verzögerung, um Race-Conditions zu vermeiden
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    }
    return;
  }

  userStore.init();

  // Starte Wartungsfenster-Polling
  wartungsfensterService.startPolling((status) => {
    wartungsfensterStore.setStatus(status.aktiv, status.meldung);

    // Zeige Benachrichtigung nur beim ersten Mal wenn Wartungsfenster aktiv wird
    if (status.aktiv && !wartungsfensterStore.sessionWarningShown) {
      toast.warning(t('warning.wartungsfenster.sessionWillBeTerminated'));
      wartungsfensterStore.setWarningShown(true);
    }
  });
});

onBeforeUnmount(() => {
  // Stoppe Polling beim Verlassen der App
  wartungsfensterService.stopPolling();
});
</script>

<template>
  <div class="h-dvh w-full flex flex-col bg-slate-50 dark:bg-slate-900">
    <!-- Header und Sidebar nur anzeigen, wenn wir nicht auf einer öffentlichen Route sind -->
    <template v-if="!isPublicRoute">
      <WartungsBanner />
      <BaseHeader />
      <BaseSidebar />
      <DnDProvider>
        <main
          class="flex-1 min-h-0 overflow-auto bg-slate-50 dark:bg-slate-900 p-8 transition-all duration-300 mt-12"
          :style="{
            marginLeft: layoutStore.isExpanded ? '256px' : '72px',
            width: `calc(100% - ${layoutStore.isExpanded ? '256px' : '72px'})`
          }"
        >
          <RouterView />
        </main>
      </DnDProvider>
    </template>

    <!-- Auf öffentlichen Routen nur den RouterView anzeigen -->
    <template v-else>
      <RouterView />
    </template>

    <Toaster
      position="top-right"
      :offset="20"
      :gap="16"
      :duration="4000"
      richColors
      :closeButton="true"
      :dismissible="true"
    />
  </div>
</template>

<style>
/* Lokale Inter-Schriftart importieren statt Google Fonts */
@import url('@/assets/fonts/inter.css');

#app {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-color);
  color: var(--text-color);
}

:root {
  --bg-color: #f8fafc;
  --text-color: #1e293b;
  --toast-border-radius: 8px;
  --toast-width: 389px;
  --toast-height: 79px;
  --toast-padding: 12px 24px;
  --toast-font-family: 'Inter', sans-serif;
  --toast-gap: 6px;
  --toast-success-color: #0f766e;
  --toast-error-color: #991b1b;
}

.dark {
  --bg-color: #0f172a;
  --text-color: #f1f5f9;
}

[data-sonner-toaster] {
  font-family: var(--toast-font-family);
}

[data-sonner-toast] {
  display: flex !important;
  width: var(--toast-width) !important;
  height: var(--toast-height) !important;
  padding: var(--toast-padding) !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: var(--toast-gap) !important;
  flex-shrink: 0 !important;
  border-radius: var(--toast-border-radius) !important;
  background: #f8fafc !important;
  box-shadow: 0px 12px 30px 0px rgba(15, 23, 42, 0.08) !important;
  pointer-events: none !important;
}

.dark [data-sonner-toast] {
  background: #1e293b !important;
  box-shadow: 0px 12px 30px 0px rgba(0, 0, 0, 0.2) !important;
}

[data-sonner-toast][data-type='success'] {
  border-left: 5px solid var(--toast-success-color) !important;
}

[data-sonner-toast][data-type='error'] {
  border-left: 5px solid var(--toast-error-color) !important;
}

[data-sonner-toast] [data-title] {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #1e293b !important;
  line-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.dark [data-sonner-toast] [data-title] {
  color: #f1f5f9 !important;
}

[data-sonner-toast]::before {
  content: 'CEETRAC Benachrichtigung';
  display: block;
  color: #64748b;
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
}

.dark [data-sonner-toast]::before {
  color: #94a3b8;
}

[data-sonner-toast]:not(.show-close) [data-close-button] {
  opacity: 0;
  pointer-events: none;
}

[data-sonner-toast].show-close [data-close-button] {
  opacity: 1;
  pointer-events: auto;
}

[data-sonner-toast] [data-icon] {
  display: none !important;
}
</style>
